import { createFileRoute } from "@tanstack/react-router";
import { marketingHead } from "@/lib/marketing/seo";
import { LegalPage, type LegalSection } from "./-components/legal-page";

export const Route = createFileRoute("/legal/delete-account")({
  component: DeleteAccount,
  head: () =>
    marketingHead({
      title: "Delete your account | People AI",
      description:
        "How to delete your People AI account, from the app, the website or by email, and what is deleted and kept.",
      path: "/v2/legal/delete-account",
    }),
});

const email = (
  <a
    className="text-foreground underline underline-offset-4"
    href="mailto:info@ppl-ai.com?subject=Delete%20my%20People%20AI%20account"
  >
    info@ppl-ai.com
  </a>
);

const SECTIONS: LegalSection[] = [
  {
    heading: "From the People AI app",
    steps: [
      "Sign in to the People AI app for Android.",
      "Open the Settings tab.",
      "Tap Delete my account, and confirm with Delete account.",
    ],
  },
  {
    heading: "From the website",
    steps: [
      "Sign in at ppl-ai.com.",
      "Open the account menu (your initials, top right) and choose Settings.",
      "Scroll to Delete my account, choose it, and confirm with Delete my account.",
    ],
  },
  {
    heading: "If you cannot sign in",
    paragraphs: [
      {
        key: "email",
        node: (
          <>
            Email {email} from the address on your account, with the subject “Delete my People AI
            account”, and tell us your name and your community. We reply to confirm it is you before
            anything is deleted.
          </>
        ),
      },
    ],
  },
  {
    heading: "What is deleted",
    points: [
      "Your login: your username, email address, password and quick sign-in code.",
      "Your profile: your name, phone number and personal settings.",
      "Every device and browser you are signed in on, and what Aria, the assistant, remembers about you.",
    ],
  },
  {
    heading: "What is kept, and for how long",
    points: [
      "Records you made as part of your work, such as resident notes, medication records, incident reports, schedules and signed documents, belong to your community. They are kept for as long as the law and your community's agreement require, and they still show who made them.",
      "The security log of who viewed or changed a record is kept for up to six years, as health privacy law requires, and then deleted.",
    ],
  },
  {
    heading: "When",
    paragraphs: [
      "Asking from the app or the website closes your account straight away: you are signed out on every device and cannot sign in again. We delete it within 30 days of your request and email you when it is done. If your community manages your account, we tell its administrator too.",
      "Changed your mind? Email info@ppl-ai.com before it is deleted and we can switch your account back on.",
    ],
  },
];

function DeleteAccount() {
  return (
    <LegalPage
      title="Delete your People AI account"
      updated="16 October 2026"
      intro="People AI, by PPL AI Inc., lets anybody with an account ask for it to be deleted, from the app, the website or by email."
      sections={SECTIONS}
    />
  );
}
